/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Per-deployment branding, from build-time env vars.
 *
 * Vite inlines `VITE_*` into the bundle, so the value is set where the bundle is
 * built, not where it runs: `frontend/admin/.env` locally, a `--build-arg` in
 * CI (the Dockerfile's `frontend-builder` stage declares the ARG). It is a
 * public display string, hence a GitHub Variable rather than a secret.
 */

/**
 * Trims the raw value and returns `null` — never a fallback literal — when it is
 * unset or blank. The header used to hardcode `'Amida Health'`, which was wrong
 * for every deployment (ADW-2936); rendering no label is the honest result of
 * missing config. Kept separate from the constant below so it is testable
 * without mutating `import.meta.env`.
 */
export function resolveOrgDisplayName(raw: string | undefined): string | null {
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

/** The configured organisation name, or `null` when none is configured. */
export const ORG_DISPLAY_NAME = resolveOrgDisplayName(import.meta.env.VITE_ORG_DISPLAY_NAME);

/**
 * Deployment environment shown beside the organisation name, matching the
 * portal's header (`VITE_ENV_LABEL`, rendered as a green success badge there).
 *
 * Defaults to 'PROD' when unset, deliberately: an unlabelled build is far more
 * likely to be production than a local one, and the failure that matters is
 * mistaking prod for dev, not the reverse.
 */
export const ENV_LABEL = import.meta.env.VITE_ENV_LABEL?.trim() || 'PROD';

/** Product name when VITE_APP_NAME is unset or blank. */
export const DEFAULT_APP_NAME = 'Learnly';

/**
 * Trims the raw value and falls back to DEFAULT_APP_NAME when it is unset or
 * blank. Same shape as resolveProductTagline: the name is a product string,
 * not a customer string, so an unconfigured build must still say something.
 */
export function resolveAppName(raw: string | undefined): string {
  const trimmed = raw?.trim();
  return trimmed ? trimmed : DEFAULT_APP_NAME;
}

/**
 * The product name everywhere the UI says it: the sidebar and sign-in
 * wordmarks, the sign-in heading, the page-title fallback, and the email
 * preference copy. Was the literal "Learnly" in each of those places.
 */
export const APP_NAME = resolveAppName(import.meta.env.VITE_APP_NAME);

/** Shown under the product wordmark when VITE_PRODUCT_TAGLINE is unset or blank. */
export const DEFAULT_PRODUCT_TAGLINE = 'Learning Management Platform';

/**
 * Trims the raw value and falls back to DEFAULT_PRODUCT_TAGLINE when it is
 * unset or blank. Unlike the organisation name this HAS a fallback: the tagline
 * describes the product, not the customer, so the default is true for every
 * deployment and a blank line under the wordmark would be the wrong result.
 * Kept separate from the constant below so it is testable without mutating
 * `import.meta.env`.
 */
export function resolveProductTagline(raw: string | undefined): string {
  const trimmed = raw?.trim();
  return trimmed ? trimmed : DEFAULT_PRODUCT_TAGLINE;
}

/**
 * Product tagline under the product wordmark in the sidebar and on the sign-in
 * brand panel. Was the literal "Amida Learning Management Platform" in both
 * places, which overflowed the 256px sidebar rail.
 */
export const PRODUCT_TAGLINE = resolveProductTagline(import.meta.env.VITE_PRODUCT_TAGLINE);
